import {
  LocationCity,
  Security,
  Map,
  Bolt,
  Receipt,
  Group,
  Person,
  Support,
  InfoOutlined,
  ArticleOutlined,
  Layers,
  Mail,
  Campaign,
  Payments,
  Leaderboard,
  VpnLock,
  WarningRounded,
  PublicOutlined,
  Redeem,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Admin, CustomRoutes, Resource } from "react-admin";
import { Route } from "react-router";

import "./styles/main.css";

import { MODULE_NAMES, queryClient } from "@/constants";
import {
  AccessLevelCreate,
  AccessLevelEdit,
  AccessLevelShow,
  AccessLevelsList,
} from "@/modules/accessLevels";
import {
  AppVersionFlagCreate,
  AppVersionFlagEdit,
  AppVersionFlagsList,
} from "@/modules/appVersionFlags";
import {
  BlacklistDeviceCreate,
  BlacklistDeviceEdit,
  BlacklistDevicesList,
} from "@/modules/blacklistDevices";
import {
  CampaignCreate,
  CampaignsList,
  CampaignEdit,
} from "@/modules/campaigns";
import { CitiesList, CityCreate, CityShow, CityEdit } from "@/modules/cities";
import {
  ClientAppCreate,
  ClientAppEdit,
  ClientAppsList,
} from "@/modules/clientApps";
import {
  CountriesList,
  CountryShow,
  CountryEdit,
  CountryCreate,
} from "@/modules/countries";
import {
  GroupCreate,
  GroupEdit,
  GroupShow,
  GroupsList,
} from "@/modules/groups";
import {
  LandingDomainCreate,
  LandingDomainsList,
  LandingDomainsShow,
} from "@/modules/landingDomains";
import {
  LandingsList,
  LandingsCreate,
  LandingsShow,
  LandingsEdit,
} from "@/modules/landings";
import { LeadReportCreate, LeadReportsList } from "@/modules/leadReport";
import {
  MailSubjectsList,
  MailSubjectEdit,
  MailSubjectCreate,
} from "@/modules/mailSubjects";
import { PremiumIssuancePage } from "@/modules/premiumIssuance";
import {
  RadiusVpnServersList,
  RadiusVpnServersShow,
  RadiusVpnServersCreate,
  RadiusVpnServersEdit,
} from "@/modules/radiusVpnServers";
import { RebillsList } from "@/modules/rebills";
import { ReceiptShow, ReceiptsList } from "@/modules/receipts";
import { ResendPromoPage } from "@/modules/resendPromo";
import { SettingsShow, SettingsEdit } from "@/modules/settings";
import { StatisticsList } from "@/modules/statistics";
import {
  TariffsList,
  TariffShow,
  TariffCreate,
  TariffEdit,
} from "@/modules/tariffs";
import { TicketShow, TicketsList } from "@/modules/tickets";
import {
  TrafficSourcesList,
  TrafficSourceShow,
  TrafficSourceEdit,
  TrafficSourceCreate,
} from "@/modules/trafficSources";
import { UserShow, UsersList, UserCreate, UserEdit } from "@/modules/users";
import { dataProvider, authProvider } from "@/providers";

import { Layout } from "./Layout";
import { CountryPricesCreate } from "./modules/countryPrice/components/CountryPricesCreate";
import { CountryPricesEdit } from "./modules/countryPrice/components/CountryPricesEdit";
import { CountryPricesList } from "./modules/countryPrice/components/CountryPricesList";

export const App = () => (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Admin
      layout={Layout}
      dataProvider={dataProvider}
      authProvider={authProvider}
      queryClient={queryClient}
    >
      <Resource
        name={MODULE_NAMES.LANDING_DOMAINS}
        options={{ label: "Landing Domains", group: "includes" }}
        list={LandingDomainsList}
        create={LandingDomainCreate}
        show={LandingDomainsShow}
      />
      <Resource
        name={MODULE_NAMES.BLACKLIST_DEVICES}
        options={{ label: "Blacklist Devices", group: "includes" }}
        list={BlacklistDevicesList}
        create={BlacklistDeviceCreate}
        edit={BlacklistDeviceEdit}
        icon={WarningRounded}
      />
      <Resource
        name={MODULE_NAMES.COUNTRIES}
        recordRepresentation=""
        list={CountriesList}
        show={CountryShow}
        edit={CountryEdit}
        create={CountryCreate}
        icon={Map}
        options={{ group: "includes" }}
      />
      <Resource
        name={MODULE_NAMES.CITIES}
        recordRepresentation=""
        list={CitiesList}
        show={CityShow}
        edit={CityEdit}
        create={CityCreate}
        icon={LocationCity}
        options={{ group: "includes" }}
      />
      <Resource
        name={MODULE_NAMES.ACCESS_LEVELS}
        list={AccessLevelsList}
        show={AccessLevelShow}
        edit={AccessLevelEdit}
        create={AccessLevelCreate}
        icon={Security}
        options={{ label: "Access Levels", group: "includes" }}
      />
      <Resource
        name={MODULE_NAMES.TRAFFIC_SOURCES}
        list={TrafficSourcesList}
        show={TrafficSourceShow}
        edit={TrafficSourceEdit}
        create={TrafficSourceCreate}
        icon={Bolt}
        options={{ label: "Sources", group: "traffic" }}
      />
      <Resource
        name={MODULE_NAMES.RECEIPTS}
        list={ReceiptsList}
        show={ReceiptShow}
        icon={Receipt}
        options={{ group: "finance" }}
      />
      <Resource
        name={MODULE_NAMES.RADIUS_VPN_SERVERS}
        list={RadiusVpnServersList}
        show={RadiusVpnServersShow}
        create={RadiusVpnServersCreate}
        edit={RadiusVpnServersEdit}
        icon={VpnLock}
        options={{ label: "VPNs", group: "main" }}
      />
      <Resource
        name={MODULE_NAMES.USERS}
        list={UsersList}
        show={UserShow}
        create={UserCreate}
        edit={UserEdit}
        icon={Person}
        options={{ group: "main" }}
      />
      <Resource
        name={MODULE_NAMES.GROUPS}
        options={{ label: "User groups", group: "main" }}
        list={GroupsList}
        show={GroupShow}
        create={GroupCreate}
        edit={GroupEdit}
        icon={Group}
      />
      <Resource
        name={MODULE_NAMES.LEAD_REPORT}
        list={LeadReportsList}
        create={LeadReportCreate}
        icon={ArticleOutlined}
        options={{ label: "Lead Report", group: "main" }}
      />
      <Resource
        name={MODULE_NAMES.TICKETS}
        list={TicketsList}
        show={TicketShow}
        icon={Support}
        options={{ label: "Tickets", group: "finance" }}
      />
      <Resource
        name={MODULE_NAMES.TARIFFS}
        options={{ label: "Tariffs", group: "finance" }}
        list={TariffsList}
        show={TariffShow}
        create={TariffCreate}
        edit={TariffEdit}
        icon={InfoOutlined}
      />
      <Resource
        name={MODULE_NAMES.LANDINGS}
        options={{ label: "Landings", group: "traffic" }}
        list={LandingsList}
        show={LandingsShow}
        edit={LandingsEdit}
        create={LandingsCreate}
        icon={Layers}
      />
      <Resource
        name={MODULE_NAMES.MAIL_SUBJECTS}
        options={{ label: "Mail Subjects", group: "traffic" }}
        list={MailSubjectsList}
        edit={MailSubjectEdit}
        create={MailSubjectCreate}
        icon={Mail}
      />
      <Resource
        name={MODULE_NAMES.CAMPAIGNS}
        options={{ group: "traffic" }}
        list={CampaignsList}
        create={CampaignCreate}
        edit={CampaignEdit}
        icon={Campaign}
      />
      <Resource
        name={MODULE_NAMES.APP_VERSION_FLAGS}
        options={{ label: "App Version Flags", group: "traffic" }}
        list={AppVersionFlagsList}
        create={AppVersionFlagCreate}
        edit={AppVersionFlagEdit}
      />
      <Resource
        name={MODULE_NAMES.CLIENT_APPS}
        options={{ label: "Client Apps", group: "traffic" }}
        list={ClientAppsList}
        edit={ClientAppEdit}
        create={ClientAppCreate}
      />
      <Resource
        name={MODULE_NAMES.STATISTICS}
        options={{ label: "Statistics", group: "traffic" }}
        list={StatisticsList}
        icon={Leaderboard}
      />
      <Resource
        name={MODULE_NAMES.REBILLS}
        options={{ label: "Rebills", group: "analytics" }}
        list={RebillsList}
        icon={Payments}
      />
      <Resource
        name={MODULE_NAMES.COUNTRY_PRICES}
        options={{ label: "Country Prices", group: "analytics" }}
        list={CountryPricesList}
        edit={CountryPricesEdit}
        create={CountryPricesCreate}
        icon={PublicOutlined}
      />

      <Resource
        name={MODULE_NAMES.PREMIUM_ISSUANCE}
        options={{ label: "Premium Issuance", group: "testing" }}
        icon={Bolt}
        list={PremiumIssuancePage}
      />
      <Resource
        name={MODULE_NAMES.RESEND_PROMO}
        options={{ label: "Resend Promo", group: "testing" }}
        icon={Redeem}
        list={ResendPromoPage}
      />
      <CustomRoutes>
        <Route path="/settings" element={<SettingsShow />} />
        <Route path="/settings/edit" element={<SettingsEdit />} />
      </CustomRoutes>
    </Admin>
  </LocalizationProvider>
);
